import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { vocabApi } from '../lib/vocabularyApi';
import { hydrateFromVocabulary } from './hydrate';

/**
 * VocabularyContext
 * -----------------
 * Fetches all org-scoped vocabularies on mount (and after admin saves),
 * hydrates the canonical design-system maps in place, and bumps a `version`
 * value. Children are keyed by version so the whole subtree remounts on
 * change — guaranteeing every consumer (including ones that don't use this
 * context) re-reads the mutated maps.
 */
const VocabularyCtx = createContext({
  vocab: null,
  version: 0,
  refresh: async () => {},
  loading: true,
});

export function useVocabulary() {
  return useContext(VocabularyCtx);
}

export function VocabularyProvider({ children }) {
  const [vocab, setVocab] = useState(null);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await vocabApi.listAll();
      hydrateFromVocabulary(data);
      setVocab(data);
      setVersion((v) => v + 1);
    } catch (e) {
      // If unauthenticated / org-less / network issue, continue with defaults.
      // The canonical maps already hold sensible defaults — the app stays
      // functional even if vocab fetch fails.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('VocabularyProvider: using defaults, reason:', e?.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <VocabularyCtx.Provider value={{ vocab, version, refresh, loading }}>
      {/* Key bump on version forces a full-subtree remount so every consumer
          (including non-hook consumers) re-renders and picks up the hydrated
          values from the canonical maps. */}
      <React.Fragment key={version}>{children}</React.Fragment>
    </VocabularyCtx.Provider>
  );
}

export default VocabularyProvider;
